import React, { useEffect, useRef, useState } from 'react';
import type { AnalysisResult } from '../types';
import {
  toMarkdown,
  toJson,
  toIssuesCsv,
  triggerDownload,
  copyToClipboard,
  suggestedFilename,
} from '../lib/export';

interface ExportMenuProps {
  result: AnalysisResult;
}

export const ExportMenu: React.FC<ExportMenuProps> = ({ result }) => {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(id);
  }, [toast]);

  const handleCopyMarkdown = async () => {
    const ok = await copyToClipboard(toMarkdown(result));
    setToast(ok ? 'Markdown copied to clipboard' : 'Copy failed — try Download instead');
    setOpen(false);
  };

  const handleDownloadMd = () => {
    triggerDownload(suggestedFilename(result, 'md'), 'text/markdown', toMarkdown(result));
    setOpen(false);
  };

  const handleDownloadJson = () => {
    triggerDownload(suggestedFilename(result, 'json'), 'application/json', toJson(result));
    setOpen(false);
  };

  const handleDownloadCsv = () => {
    triggerDownload(suggestedFilename(result, 'csv'), 'text/csv', toIssuesCsv(result));
    setOpen(false);
  };

  const handlePrint = () => {
    setOpen(false);
    // Tiny defer so the menu actually closes before print preview
    setTimeout(() => window.print(), 50);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }} className="export-menu no-print">
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Export
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden style={{ marginLeft: 2 }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 240,
            background: 'var(--paper)',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            boxShadow: 'var(--shadow-2)',
            padding: '6px 0',
            zIndex: 50,
          }}
        >
          <MenuItem label="Copy as Markdown" hint="Best for AI assistants & docs" onClick={handleCopyMarkdown} />
          <MenuItem label="Download Markdown (.md)" onClick={handleDownloadMd} />
          <MenuItem label="Download JSON (.json)" hint="Raw analysis data" onClick={handleDownloadJson} />
          <MenuItem label="Download Issues CSV (.csv)" hint="Triage in Sheets/Excel" onClick={handleDownloadCsv} />
          <Divider />
          <MenuItem label="Print / Save as PDF" hint="Cmd+P" onClick={handlePrint} />
        </div>
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            background: 'var(--ink)',
            color: 'var(--paper)',
            padding: '8px 14px',
            borderRadius: 4,
            fontSize: 13,
            whiteSpace: 'nowrap',
            zIndex: 60,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

const MenuItem: React.FC<{ label: string; hint?: string; onClick: () => void }> = ({ label, hint, onClick }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    style={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      background: 'transparent',
      border: 0,
      padding: '10px 16px',
      cursor: 'pointer',
      font: 'inherit',
      color: 'var(--ink)',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-2)')}
    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
  >
    <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
    {hint && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{hint}</div>}
  </button>
);

const Divider: React.FC = () => (
  <div role="separator" style={{ height: 1, background: 'var(--rule)', margin: '6px 0' }} />
);
